import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { History, Plus, Pencil, Trash2, Clock, FileDown } from "lucide-react";

const ACTION_LABELS = { created: "Création", updated: "Modification", deleted: "Suppression", report_generated: "Rapport PDF généré" };
const ACTION_ICONS = { created: Plus, updated: Pencil, deleted: Trash2, report_generated: FileDown };

const formatDateTime = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "";

const FIELD_LABELS_FALLBACK = (k) => k.replace(/_/g, " ");
const HIDDEN_FIELDS = new Set(["updated_at", "created_at", "id", "organization_id"]);

export default function AuditHistoryPanel({ open, onClose, entityType, entityId, title }) {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["audit-logs", entityType, entityId],
    queryFn: () => api.get(`/audit-logs?entity_type=${entityType}&entity_id=${entityId}`).then((r) => r.data),
    enabled: open && !!entityId,
    staleTime: 0,
    refetchOnMount: "always",
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="audit-history-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History size={16} className="text-[#023E73]" /> Historique — {title}</DialogTitle>
        </DialogHeader>
        {isLoading && <div className="text-sm text-slate-400 py-6 text-center">Chargement…</div>}
        {!isLoading && logs.length === 0 && (
          <div className="text-sm text-slate-400 py-6 text-center" data-testid="audit-history-empty">Aucun historique disponible.</div>
        )}
        <div className="space-y-3 mt-1">
          {logs.map((l) => {
            const Icon = ACTION_ICONS[l.action] || Clock;
            const changedFields = l.new_values && typeof l.new_values === "object"
              ? Object.keys(l.new_values).filter((k) => !HIDDEN_FIELDS.has(k))
              : [];
            return (
              <div key={l.id} data-testid={`audit-entry-${l.id}`} className="flex gap-3 text-sm border-b border-slate-100 pb-3 last:border-0">
                <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon size={13} className="text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{ACTION_LABELS[l.action] || FIELD_LABELS_FALLBACK(l.action)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {l.user_name || "Système"} • {formatDateTime(l.created_at)}
                  </div>
                  {changedFields.length > 0 && (
                    <div className="text-xs text-slate-500 mt-1">
                      Champs modifiés : {changedFields.map(FIELD_LABELS_FALLBACK).join(", ")}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
